import { useState, useEffect, useRef } from 'react';
// UserProfile is Clerk's drop-in account manager: password, email addresses,
// connected accounts, 2FA. Without it a team member has no way to set or
// change their own password from inside the app — the only route was Clerk's
// hosted pages, which nothing linked to.
import { useAuth, useUser, UserProfile } from '@clerk/clerk-react';
import { BRAND } from '../config/brand';

const TITLES = { dashboard:'Dashboard', search:'Search Products', presentations:'Presentations',
  quotes:'Quote Requests', storefront:'My Storefront', settings:'Settings' };

export default function Topbar({ dist, screen, cartCount, navigate }) {
  // Account menu behind the company name / avatar. Before this there was no
  // way to sign out anywhere in the app — the top-right corner is where people
  // look first, so the whole company+avatar cluster is the trigger rather than
  // just the avatar circle (a 30px target is easy to miss).
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const wrapRef = useRef(null);
  const { signOut } = useAuth();
  const { user } = useUser();

  // Close on outside click and on Escape — a menu that only closes by
  // re-clicking the trigger feels broken.
  // Escape closes the account modal wherever it's open.
  useEffect(() => {
    if (!showProfile) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowProfile(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showProfile]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Clerk's signOut clears the session; the redirect to root then re-renders
  // into the signed-out <SignIn/> state in App.jsx.
  const handleSignOut = async () => {
    setOpen(false);
    try { if (typeof signOut === 'function') await signOut(); }
    catch { /* redirect regardless so the user isn't stuck */ }
    window.location.href = '/';
  };

  const email = user?.primaryEmailAddress?.emailAddress || dist?.email || null;
  const displayName = user?.fullName || dist?.name || null;

  const itemStyle = {
    display:'flex', alignItems:'center', gap:8, width:'100%', textAlign:'left',
    background:'none', border:'none', padding:'9px 14px', fontSize:13,
    fontFamily:'inherit', cursor:'pointer', color:'#334155',
  };

  return (
    <div style={{ background:'#fff', borderBottom:'1px solid #E2E8F0', padding:'0 24px', height:56,
      display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ fontSize:15, fontWeight:500, color:'#0F172A' }}>{TITLES[screen]||screen}</span>
        {cartCount>0 && screen!=='presentations' && (
          <span onClick={() => navigate('presentations')}
            style={{ background:BRAND.colors.primary, color:'#fff',
              fontSize:13, fontWeight:700, padding:'6px 16px', borderRadius:20, cursor:'pointer',
              boxShadow:`0 2px 10px ${BRAND.colors.primary}66`, display:'inline-flex', alignItems:'center', gap:7,
              letterSpacing:'0.2px' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 3px 14px ${BRAND.colors.primary}99`; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 2px 10px ${BRAND.colors.primary}66`; }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#fff', flexShrink:0 }}/>
            {cartCount} item{cartCount===1?'':'s'} pending build — continue presentation →
          </span>
        )}
      </div>

      <div ref={wrapRef} style={{ position:'relative' }}>
        <div onClick={() => setOpen(o => !o)}
          role="button" tabIndex={0} aria-haspopup="menu" aria-expanded={open}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
          style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer',
            padding:'4px 6px', borderRadius:8, background: open ? '#F1F5F9' : 'transparent',
            transition:'background 0.15s', userSelect:'none' }}>
          <span style={{ fontSize:13, color:'#64748B' }}>{dist?.company}</span>
          <div style={{ width:30, height:30, borderRadius:'50%', background:BRAND.colors.primary,
            display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:600, fontSize:13 }}>
            {(dist?.company||BRAND.name)[0].toUpperCase()}
          </div>
          <span style={{ fontSize:9, color:'#94A3B8', transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}>▼</span>
        </div>

        {open && (
          <div role="menu" style={{ position:'absolute', top:'calc(100% + 6px)', right:0, minWidth:220,
            background:'#fff', border:'1px solid #E2E8F0', borderRadius:10,
            boxShadow:'0 8px 24px rgba(15,23,42,0.12)', padding:'6px 0', zIndex:100 }}>

            {/* Identity block: which company AND which user, since several
                people can be signed into the same distributor account. */}
            <div style={{ padding:'8px 14px 10px', borderBottom:'1px solid #F1F5F9' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#0F172A' }}>{dist?.company || 'Your account'}</div>
              {(displayName || email) && (
                <div style={{ fontSize:11, color:'#94A3B8', marginTop:2, wordBreak:'break-all' }}>
                  {displayName && email ? `${displayName} · ${email}` : (displayName || email)}
                </div>
              )}
            </div>

            <button type="button" role="menuitem" onClick={() => { setOpen(false); setShowProfile(true); }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              style={itemStyle}>
              <span style={{ fontSize:13 }}>👤</span> Manage account
            </button>

            <button type="button" role="menuitem" onClick={() => { setOpen(false); navigate('settings'); }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              style={itemStyle}>
              <span style={{ fontSize:13 }}>⚙️</span> Settings
            </button>

            <div style={{ height:1, background:'#F1F5F9', margin:'4px 0' }}/>

            <button type="button" role="menuitem" onClick={handleSignOut}
              onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              style={{ ...itemStyle, color:'#B91C1C' }}>
              <span style={{ fontSize:13 }}>↩</span> Sign out
            </button>
          </div>
        )}
      </div>

      {/* Account modal. Clerk owns everything inside it — we only provide the
          overlay and a way out. Closing on the backdrop or Escape, not just a
          button, since it covers the whole app. */}
      {showProfile && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowProfile(false); }}
          style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', zIndex:200,
            display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 16px', overflowY:'auto' }}>
          <div style={{ position:'relative' }}>
            <button onClick={() => setShowProfile(false)}
              aria-label="Close account settings"
              style={{ position:'absolute', top:-34, right:0, background:'none', border:'none',
                color:'#fff', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
              Close ✕
            </button>
            <UserProfile routing="virtual" />
          </div>
        </div>
      )}
    </div>
  );
}
